import { ArrowRight, BookOpenText, Sparkles, Star, StickyNote, User } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { InvestigationSource } from '@/types/investigation'
import { cn } from '@/utils/cn'
import { formatRelative } from '@/utils/date'

const PREVIEW_LIMIT = 3

/**
 * The slice of an Investigation this card actually renders. `Investigation`
 * is structurally assignable to it, so the connected card passes store data
 * straight through and stories mock only these fields.
 */
export interface SavedInvestigationSummary {
  id: string
  title: string
  source: InvestigationSource
  notes: readonly unknown[]
  includedInReport: boolean
  /** ISO-8601 UTC */
  updatedAt: string
}

export interface SavedInvestigationsCardViewProps {
  /** Saved investigations for the case, in any order. */
  saved: SavedInvestigationSummary[]
  caseId: string
}

/**
 * Pure presentation for the overview's saved-investigations summary. The
 * connected card in `features/case-overview` supplies `saved` from the store.
 */
export function SavedInvestigationsCardView({ saved, caseId }: SavedInvestigationsCardViewProps) {
  const { t, i18n } = useTranslation()
  const justNow = t('overview.justNow')

  const stats = useMemo(() => {
    const total = saved.length
    let withNotes = 0
    let inReport = 0
    let lastUpdatedAt = 0
    for (const inv of saved) {
      if (inv.notes.length > 0) withNotes += 1
      if (inv.includedInReport) inReport += 1
      const ts = new Date(inv.updatedAt).getTime()
      if (ts > lastUpdatedAt) lastUpdatedAt = ts
    }
    return { total, withNotes, inReport, lastUpdatedAt }
  }, [saved])

  const recent = useMemo(
    () =>
      [...saved]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, PREVIEW_LIMIT),
    [saved]
  )

  const linkTo = `/cases/${caseId}/investigations`

  return (
    <Card className="p-4 md:p-6">
      {/* Phone: the CTA drops to its own full-width row so the copy keeps the line. */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 md:flex-nowrap md:gap-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
          <BookOpenText className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">
            {t('overview.savedInvestigations.title')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {stats.total === 0
              ? t('overview.savedInvestigations.empty')
              : t('overview.savedInvestigations.summary')}
          </p>
        </div>
        <Button asChild variant="outline" size="xs" className="shrink-0 max-md:h-9 max-md:w-full">
          <Link to={linkTo}>
            {t('overview.savedInvestigations.viewAll')}
            <ArrowRight className="size-3" />
          </Link>
        </Button>
      </div>

      {stats.total > 0 && (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground max-md:gap-x-4">
            <span className="inline-flex items-center gap-1.5">
              <BookOpenText className="size-3.5" />
              <span className="font-mono tabular-nums text-foreground">{stats.total}</span>
              <span>{t('investigations.page.stats.saved').toLowerCase()}</span>
            </span>
            <span className="inline-block size-1 rounded-full bg-border max-md:hidden" />
            <span className="inline-flex items-center gap-1.5">
              <StickyNote className="size-3.5" />
              <span className="font-mono tabular-nums text-foreground">{stats.withNotes}</span>
              <span>{t('investigations.page.stats.withNotes').toLowerCase()}</span>
            </span>
            <span className="inline-block size-1 rounded-full bg-border max-md:hidden" />
            <span className="inline-flex items-center gap-1.5">
              <Star className="size-3.5" />
              <span className="font-mono tabular-nums text-foreground">{stats.inReport}</span>
              <span>{t('investigations.page.stats.inReport').toLowerCase()}</span>
            </span>
            {stats.lastUpdatedAt > 0 && (
              <>
                <span className="inline-block size-1 rounded-full bg-border max-md:hidden" />
                <span>
                  {t('overview.savedInvestigations.lastUpdated', {
                    when: formatRelative(
                      new Date(stats.lastUpdatedAt).toISOString(),
                      i18n.language,
                      justNow
                    ),
                  })}
                </span>
              </>
            )}
          </div>

          <ul className="mt-5 space-y-1.5">
            {recent.map(inv => (
              <RecentRow key={inv.id} inv={inv} caseId={caseId} />
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}

function RecentRow({ inv, caseId }: { inv: SavedInvestigationSummary; caseId: string }) {
  const { t, i18n } = useTranslation()
  const justNow = t('overview.justNow')
  const isAi = inv.source === 'ai'
  return (
    <li>
      <Link
        to={`/cases/${caseId}/investigations/${inv.id}`}
        className="group/row flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted max-md:flex-wrap max-md:gap-y-1"
      >
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-md border',
            isAi
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border bg-muted text-muted-foreground'
          )}
        >
          {isAi ? <Sparkles className="size-3.5" /> : <User className="size-3.5" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground max-md:line-clamp-2 max-md:whitespace-normal">
          {inv.title}
        </span>
        {/* Phone: the meta drops to a second line under the title (the wrapper
            is `contents` from md, so the desktop row is unchanged). */}
        <span className="flex items-center gap-3 max-md:order-last max-md:basis-full max-md:pl-10 md:contents">
          {inv.notes.length > 0 && (
            <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground">
              <StickyNote className="size-3" />
              <span className="font-mono tabular-nums">{inv.notes.length}</span>
            </span>
          )}
          {inv.includedInReport && (
            <span title={t('investigations.page.featured.inReport')} className="text-primary">
              <Star className="size-3 fill-current" />
            </span>
          )}
          <span className="font-mono text-2xs text-muted-foreground">
            {formatRelative(inv.updatedAt, i18n.language, justNow)}
          </span>
        </span>
        <ArrowRight className="size-3 text-muted-foreground max-md:shrink-0 transition-transform group-hover/row:translate-x-0.5" />
      </Link>
    </li>
  )
}
